import type { User } from '../types';
import { jwtDecode } from './jwtHelpers';

/**
 * Builds the session user from an access token, in the same shape `login`
 * gets from the backend.
 *
 * The token signs the display name as `username` (Auth login/refresh
 * `generateTokens`), while the login response's `user` carries `name`. A
 * session restored from the token (any page reload) therefore had no `name`,
 * and every `user.name` reader — the sidebar's profile among them — fell back
 * to the email.
 *
 * Returns null for a token that does not decode.
 */
export function userFromToken(token: string): User | null {
  const claims = jwtDecode(token);
  if (!claims) return null;
  // CSI-344: a Keycloak token names the user `sub`, the roles `roles` (array)
  // and the login `preferred_username`; the legacy token `id`, `role` and
  // `username`. Same User either way.
  const roles: unknown = claims.roles;
  return {
    ...claims,
    id: claims.id ?? claims.sub,
    role: claims.role ?? (Array.isArray(roles) ? roles[0] : undefined),
    email: claims.email ?? claims.preferred_username ?? '',
    name: claims.name || claims.username || claims.preferred_username || '',
  };
}
